import { useSelector } from 'react-redux';
import {
  createConfigSelector, getActiveLocaleSelector, getHasAuthLoadedSelector, getIsLoggedInSelector,
} from '@americanexpress/travel-selectors';
import { useErrorOnNotOkFetchye } from '@americanexpress/fetchye-amex';
import { v4 } from 'uuid';
import { ignoreHeadersByKey } from 'fetchye';
import { locales } from '@americanexpress/conditional-features';
import { MODULE_NAME } from '../constants/modules';
import { useMembershipData } from './useMembershipDataHook';
import { useFeatureFlagWithProperties } from '../thunks/useFeatureFlagWithProperties';
import { PERSONA_API_FF } from '../constants/oneXpConstants';
import { getExploreMoreFromPersona, getMarketingArticlesFromPersona } from '../utils/personaUtils';

export const getPersonaAccounts = (membershipApiData) => {
  if (!membershipApiData?.data?.membership) {
    return [];
  }

  const accounts = [];

  membershipApiData.data.membership.forEach((value) => {
    const account = {
      iaCode: value?.demographics?.product.productId,
      isBasic: value?.demographics?.product.isBasic,
    };
    accounts.push(account);
  });

  return accounts;
};

export const fetchyeReporting = ({ key, options, error }) => ({
  moduleID: MODULE_NAME,
  component: 'usePersonaApi',
  fetch: {
    url: key,
    status: error?.status,
    correlationId: options.headers?.correlationId || '',
  },
});

export const usePersonaApi = () => {
  const hasPersona = useFeatureFlagWithProperties(PERSONA_API_FF);

  const guestPersonaApiEndpoint = useSelector(createConfigSelector('personaApiGuest'));
  const userPersonaApiEndpoint = useSelector(createConfigSelector('personaApiUser'));
  const hasAuthLoaded = useSelector(getHasAuthLoadedSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const locale = useSelector(getActiveLocaleSelector);
  const {
    isLoading: isMembershipApiLoading,
    data: membershipApiData,
    error: membershipApiError,
  } = useMembershipData();

  const cards = getPersonaAccounts(membershipApiData);

  const useUserEndpoint = (hasAuthLoaded && isLoggedIn)
    && (!isMembershipApiLoading && !membershipApiError);

  const defer = (!hasPersona && locale !== locales.EN_US)
    || !hasAuthLoaded
    || isMembershipApiLoading;

  const {
    data,
    isLoading: isFetchyeLoading,
    errors,
  } = useErrorOnNotOkFetchye(
    useUserEndpoint ? userPersonaApiEndpoint : guestPersonaApiEndpoint,
    {
      headers: {
        'Content-Type': 'application/json',
        correlationId: v4(),
        clientId: '684C957199C3BE6C153A778D1986032B',
        locale,
      },
      mapOptionsToKey: ignoreHeadersByKey(['correlationId']),
      defer,
      credentials: 'include',
      ...useUserEndpoint && {
        method: 'POST',
        body: JSON.stringify({
          cards,
          dataType: 'ALL',
        }),
      },
    },
    fetchyeReporting
  );

  const isLoading = isFetchyeLoading
    || isMembershipApiLoading
    || !hasAuthLoaded;

  return {
    data: data?.body,
    isLoading,
    errors: errors || (data && data.status !== 200),
  };
};

export const usePersonaArticles = () => {
  const { data: personaData, isLoading, error } = usePersonaApi();
  const { articles, heading } = getMarketingArticlesFromPersona(personaData);
  const displayArticles = articles.slice(0, 3);
  const mappedArticles = displayArticles.reduce((acc, item, index) => {
    const article = {
      id: `for-you-${index + 1}`,
      title: item.heading,
      attribution: item.imageLocationLabel,
      imageUrl: item.imageUrl,
      body: item.bodyText,
      ctaLabel: item.ctaLabel,
      ctaDestination: item.redirectUrl,
      showCtaButton: item.showCtaButton,
      analyticElement: item.analyticElement,
    };
    // before last article, insert ads tile
    if (displayArticles.length > 2 && index === displayArticles.length - 1) {
      return [
        ...acc,
        { id: 'for-you-ad', channelName: 'TLS', placementName: 'HOMEPAGE_FORYOU' },
        { ...article, id: `for-you-${index + 2}` },
      ];
    }
    // if only 2 articles, insert ads tile at the end
    if (displayArticles.length < 3 && index === displayArticles.length - 1) {
      return [
        ...acc,
        article,
        { id: 'for-you-ad', channelName: 'TLS', placementName: 'HOMEPAGE_FORYOU' },
      ];
    }
    return [...acc, article];
  }, []);

  return {
    heading,
    articles: mappedArticles,
    personaType: personaData?.personas[0].personaType,
    isLoading,
    error,
  };
};

export const usePersonaExploreMore = () => {
  const { data: personaData, isLoading, error } = usePersonaApi();
  const { articles, heading } = getExploreMoreFromPersona(personaData);
  const mappedArticles = articles.reduce((acc, item, index) => {
    const article = {
      id: `explore-more-${index + 1}`,
      title: item.heading,
      attribution: item.imageLocationLabel,
      imageUrl: item.imageUrl,
      body: item.bodyText,
      ctaLabel: item.ctaLabel,
      ctaDestination: item.redirectUrl,
      showCtaButton: item.showCtaButton,
      analyticElement: item.analyticElement,
    };
    return [...acc, article];
  }, []);

  return {
    heading,
    articles: mappedArticles,
    personaType: personaData?.personas[0].personaType,
    isLoading,
    error,
  };
};
